import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { EvidenceWrapper } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { usePaginateItemsWithNavigate } from 'src/components/Reusable/paginate/paginate.hooks'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import log from 'loglevel'
import { useSearchContext } from 'src/Utils/search/search.context'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { EvidenceToControlMappingInfo } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_pb'
import {
  useEvidenceIndexPage,
  useGetEvidenceIndexPageRequest,
  useSearchableEvidence,
} from '../evidence.hooks'
import { EVIDENCE_INDEX_PAGE_SIZE } from '../evidence.constants'

export const useEvidencePagination = (): {
  hasPageParam: boolean
  pageCount: number
} => {
  const [searchParams] = useSearchParams()
  const pageCount = Number(searchParams.get('pc'))
  const queryParams: ParsedQuery<string> = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const hasPageParam = !!queryParams[FilterParam.PAGE]

  return {
    hasPageParam,
    pageCount,
  }
}

export const useEvidenceGridData = (): {
  evidence: EvidenceWrapper[]
  hasEvidence: boolean
  isLoading: boolean
  error: Error | null
  onUpdate: () => Promise<void>
  paginatedItems: EvidenceWrapper[]
  showPaginated: boolean
  matchingEvidence: EvidenceWrapper[]
  currentPage: number
  prevPage: () => void
  nextPage: () => void
  pageCount: number
} => {
  const { hasPageParam, pageCount } = useEvidencePagination()
  const req = useGetEvidenceIndexPageRequest()
  const { data, isLoading, error, mutate } = useEvidenceIndexPage(req)

  const onUpdate = useCallback(async () => {
    await mutate()
  }, [mutate])

  useEffect(() => {
    if (!data && error) {
      showInfoToast(
        "Sorry we couldn't load your evidence. Please refresh and try again.",
      )
      log.error('Error loading evidence', error)
    }
  }, [data, error])

  const evidence = useMemo(() => data?.getItemsList() || [], [data])
  const hasEvidence = data?.getHasEvidence() || false
  const searchableEvidence = useSearchableEvidence(evidence)
  const { data: matchingEvidence, contentMatches } =
    useApplySearch(searchableEvidence)
  const { setContentMatchIds } = useSearchContext()

  useEffect(() => {
    setContentMatchIds(contentMatches)
  }, [contentMatches, setContentMatchIds])

  const { paginatedItems, currentPage, prevPage, nextPage } =
    usePaginateItemsWithNavigate<EvidenceWrapper>({
      items: matchingEvidence,
      pageSize: pageCount > 0 ? pageCount : EVIDENCE_INDEX_PAGE_SIZE,
    })

  const showPaginated = hasPageParam && matchingEvidence.length > 0

  return {
    evidence,
    hasEvidence,
    isLoading,
    error,
    onUpdate,
    paginatedItems,
    showPaginated,
    matchingEvidence,
    currentPage,
    prevPage,
    nextPage,
    pageCount,
  }
}

export const useSearchableEvidenceToControlMappingInfo = (
  evidenceToControlMappingInfo: EvidenceToControlMappingInfo[],
): SearchableData<EvidenceToControlMappingInfo>[] => {
  return useMemo(() => {
    return prepareSearchableData(
      evidenceToControlMappingInfo,
      (evidenceToControlMappingInfo: EvidenceToControlMappingInfo) =>
        evidenceToControlMappingInfo.getEvidence()?.getId() || '',
      (evidenceToControlMappingInfo: EvidenceToControlMappingInfo) => {
        return [
          evidenceToControlMappingInfo.getEvidence()?.getCaption(),
          evidenceToControlMappingInfo
            .getLinkedControlsList()
            .length.toString(),
          evidenceToControlMappingInfo
            .getSuggestedControlsList()
            .length.toString(),
          evidenceToControlMappingInfo
            .getLinkedControlsList()
            .map(
              (linkedControl) =>
                `${linkedControl.getModelId()} ${linkedControl.getName()}`,
            )
            .join(', '),
          evidenceToControlMappingInfo
            .getSuggestedControlsList()
            .map(
              (suggestedControl) =>
                `${suggestedControl.getModelId()} ${suggestedControl.getName()}`,
            )
            .join(', '),
        ]
      },
    )
  }, [evidenceToControlMappingInfo])
}
